import React, { useEffect, useState } from 'react'
import { ViewProps } from '../calendar/types'
import style from './WeekView.module.css'
import dayjs from 'dayjs'
import { dateToDateString, getMonday } from '../../common/utils/date'
import { Event as E } from '../../modules/calendar/models/Event'
import { Query, QueryParam } from '../../common/api/Query'
import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { getUserContainer } from '../../container/user-module'
import { capitalize, reduceString } from '../../common/utils/strings'
import { CalendarModal } from '../calendar/CalendarModal'
import { ICircleService } from '../../modules/users/services/CircleService'
import { CIRCLE_SERVICE_KEY, LOGGED_USER_SERVICE_KEY } from '../../modules/users/container'
import { getCalendarContainer } from '../../container/calendar-module'
import { EventService } from '../../modules/calendar/services/EventService'
import { EVENT_SERVICE_KEY } from '../../modules/calendar'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { useTranslation } from 'react-i18next'
import addIcon from '../../assets/calendar/add.svg'
import { navigate, useLocation } from '@reach/router'
import { ROUTE_CALENDAR_FORM, ROUTE_CREATE } from '../../routes/routes-constants'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { EventCategory } from '../../modules/calendar/enums/EventCategory'
import reviewIcon from '../../assets/events_icons/bola-revision.svg'
import dateIcon from '../../assets/calendar/cita.svg'
import vaccineIcon from '../../assets/events_icons/bola-vacunas.svg'
import adviceIcon from '../../assets/events_icons/bola-consejos.svg'
import landmarkIcon from '../../assets/events_icons/bola-hitos.svg'
import trainingIcon from '../../assets/calendar/ejercicio.svg'
import formIcon from '../../assets/events_icons/evento-formulario.svg'
import othersIcon from '../../assets/events_icons/bola-otros.svg'
import medicationIcon from '../../assets/events_icons/bola-tratamientos.svg'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { PageLayout } from 'pages/layout'

dayjs.extend(utc)
dayjs.extend(timezone)

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface WeekViewProps extends ViewProps {
  id: string | undefined
}

interface WeekEventProps {
  event: E
  onEventClick: (event: E) => void
  title: (key: string) => string
  eventCount: number
}

interface WeekHeaderProps {
  weekDays: Date[]
}

interface EventIconProps {
  type: EventCategory
}

interface UseWeekViewDataProps {
  selectedDate: Date
  userId: string
}

interface UseWeekViewDataReturn {
  events: E[]
  monday: Date
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
}

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const userContainer = getUserContainer()
const calendarContainer = getCalendarContainer()
const eventService = calendarContainer.get<EventService>(EVENT_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

const EVENT_COLORS: Record<number, string> = {
  1: '#4582C8',
  2: '#E36017',
  3: '#d2617e',
  4: '#eadf33',
  5: '#3fbfcc',
  6: 'var(--green-light)',
  7: '#815abb',
  8: '#9b8c58',
}

const DEFAULT_COLOR = '#959595'

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateHours = (): string[] => {
  const hourRange = []
  for (let i = 7; i < 23; i++) {
    hourRange.push(i < 10 ? `0${i}:00` : `${i}:00`)
  }
  return hourRange
}

const generateWeekDays = (monday: Date): Date[] => {
  const datesRange = [monday]
  for (let i = 1; i < 7; i++) {
    const newDate = new Date(monday)
    newDate.setDate(monday.getDate() + i)
    datesRange.push(newDate)
  }
  return datesRange
}

const getColorByCategory = (category: number): string => {
  return EVENT_COLORS[category] || DEFAULT_COLOR
}

const shouldShowEvent = (event: E, day: Date, hour: string): boolean => {
  // Convertir de UTC a hora local del sistema
  const eventStart = dayjs.utc(event.startDate).local()
  const currentDay = dayjs(day)
  const currentHour = parseInt(hour.split(':')[0])

  const isSameWeek = eventStart.isSame(currentDay, 'week')
  const isSameDay = eventStart.isSame(currentDay, 'day')
  const eventHour = eventStart.hour()

  return isSameWeek && isSameDay && currentHour === eventHour
}

const calculateEventDuration = (event: E): number | undefined => {
  const startLocal = dayjs.utc(event.startDate).local()
  const finishLocal = dayjs.utc(event.finishDate).local()

  if (finishLocal.format('DD') === startLocal.format('DD')) {
    return finishLocal.hour() - startLocal.hour()
  }
  return undefined
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useWeekViewData = ({ selectedDate, userId }: UseWeekViewDataProps): UseWeekViewDataReturn => {
  const [events, setEvents] = useState<E[]>([])
  const [monday, setMonday] = useState<Date>(getMonday(selectedDate))
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    const finishDate = new Date(monday)
    finishDate.setDate(finishDate.getDate() + 7)

    eventService
      .getFilteredList(
        new Query({
          query: [
            new QueryParam<E>('startDate', monday.toISOString()),
            new QueryParam<E>('finishDate', finishDate.toISOString()),
          ],
        }),
        userId
      )
      .subscribe((res) => {
        setEvents([...res.items])
      })
  }, [monday, isLoading, userId])

  useEffect(() => {
    const tmpMonday = getMonday(selectedDate)
    if (tmpMonday.getTime() !== monday.getTime()) {
      setMonday(tmpMonday)
    }
  }, [selectedDate, monday])

  return {
    events,
    monday,
    isLoading,
    setIsLoading,
  }
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

const EventIcon: React.FC<EventIconProps> = ({ type }) => {
  const { t } = useTranslation()

  const getIconData = (eventType: EventCategory) => {
    switch (eventType) {
      case EventCategory.Reviews:
        return { src: reviewIcon, alt: 'reviews', title: t('reviews') || 'reviews' }
      case EventCategory.Medication:
        return { src: medicationIcon, alt: 'treatment', title: t('treatment') || 'treatment' }
      case EventCategory.Vaccines:
        return { src: vaccineIcon, alt: 'vaccine', title: t('vaccine') || 'vaccine' }
      case EventCategory.Appointment:
        return {
          src: dateIcon,
          alt: 'medicalappointment',
          title: t('medicalappointment') || 'medicalappointment',
        }
      case EventCategory.Advices:
        return { src: adviceIcon, alt: 'advices', title: t('advices') || 'advices' }
      case EventCategory.Landmarks:
        return { src: landmarkIcon, alt: 'landmarks', title: t('landmarks') || 'landmarks' }
      case EventCategory.Training:
        return { src: trainingIcon, alt: 'training', title: t('training') || 'training' }
      case EventCategory.Forms:
        return { src: formIcon, alt: 'form', title: t('form') || 'form' }
      default:
        return { src: othersIcon, alt: 'others', title: t('others') || 'others' }
    }
  }

  const iconData = getIconData(type)

  return (
    <Tooltip title={iconData.title}>
      <img src={iconData.src} alt={iconData.alt} />
    </Tooltip>
  )
}

const WeekEvent: React.FC<WeekEventProps> = ({ event, onEventClick, title, eventCount }) => {
  const duration = calculateEventDuration(event)
  const isSingleHour = !duration || duration <= 1
  const widthPercent = `${100 / eventCount}%`

  const formatEventTitle = (event: E): string => {
    const localStartDate = dayjs.utc(event.startDate).local()
    const maxChars = isSingleHour ? 15 : 30 // ← más caracteres si dura más de 1h
    return `${localStartDate.format('HH:mm')} ${reduceString(title(event.title), maxChars)}`
  }

  const eventTitle = formatEventTitle(event)
  const backgroundColor = getColorByCategory(event.eventCategory)
  const textColor = 'var(--white)'

  if (isSingleHour) {
    return (
      <Box
        className={style.eventNotSameDay}
        onClick={() => onEventClick(event)}
        style={{ backgroundColor, color: textColor }}
      >
        <div className={style.iconEventCategoryNotSameDay}>
          <EventIcon type={event.eventCategory} />
        </div>
        {eventCount === 1 && (
          <h3 className={style.eventTitleNotSameDay} style={{ color: textColor }}>
            {eventTitle}
          </h3>
        )}
      </Box>
    )
  }

  return (
    <Box
      className={style.event}
      onClick={() => onEventClick(event)}
      style={{
        ['--duration-height' as any]: (duration ?? 1) - 1,
        ['--col' as any]: backgroundColor,
        backgroundColor,
        color: textColor,
      }}
    >
      <div className={style.iconEventCategory}>
        <EventIcon type={event.eventCategory} />
      </div>
      {eventCount === 1 && (
        <h3 className={style.eventTitle} style={{ color: textColor }}>
          {eventTitle}
        </h3>
      )}
    </Box>
  )
}

const WeekHeader: React.FC<WeekHeaderProps> = ({ weekDays }) => {
  return (
    <TableHead>
      <TableRow>
        <TableCell
          className={style.weekHeader}
          style={{
            visibility: 'hidden',
            width: '37px',
            padding: '0',
            margin: '0',
          }}
        >
          <div className={style.hour} style={{ marginRight: 18 }}></div>
        </TableCell>
        {weekDays.map((d, i) => (
          <TableCell className={style.weekHeader} key={dateToDateString(d)}>
            <div className={i === 0 ? style.day1 : ''}>
              <div
                className={
                  new Date(d).getDay() === new Date().getDay() ? style.weekNoDay : style.weekDay
                }
              >
                <div className={style.weekDayNumber}>{dayjs(d).format('DD')}</div>
                <div className={style.weekDayName}>
                  {window.innerWidth > 480
                    ? capitalize(dayjs(d).locale(navigator.language).format('dddd'))
                    : capitalize(dayjs(d).locale(navigator.language).format('dd'))}
                </div>
              </div>
            </div>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function WeekViewHome(props: WeekViewProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const { openMenuLeft } = useIsOpenMenuLeftContext()

  // State management
  const [selectedEvent, setSelectedEvent] = useState<E>()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [date, setDate] = useState<Date>(props.selectedDate || new Date())

  // Data and services
  const loggedUser = loggedUserService.get()
  const circle = userContainer.get<ICircleService>(CIRCLE_SERVICE_KEY).getActive()
  const { events, monday, isLoading, setIsLoading } = useWeekViewData({
    selectedDate: props.selectedDate,
    userId: loggedUser?.id || '',
  })

  // Computed values
  const hours = generateHours()
  const weekDays = generateWeekDays(monday)

  // Event handlers
  const handleCloseModal = () => setOpenModal(false)

  const handleEventClick = (event: E) => {
    setSelectedEvent(event)
    setOpenModal(true)
  }

  const editEvent = (e: E) => {
    navigate(`${ROUTE_CALENDAR_FORM}/${e.id}`, { state: { date } })
    setOpenModal(false)
  }

  const deleteEvent = (e: E) => {
    if (e.id) {
      eventService.delete(e.id).subscribe(() => {
        setDate(new Date(date))
        setIsLoading(!isLoading)
      })
    }
    setOpenModal(false)
  }

  const createEvent = () => {
    navigate(`${ROUTE_CALENDAR_FORM}/${ROUTE_CREATE}`, {
      state: { date, currentUrl: location.pathname },
    })
  }

  // Render
  return (
    <PageLayout>
      <Table
        sx={{
          borderRadius: '27px',
          border: 'none',
          background: 'transparent',
          width: 'auto',
          '& td': {
            borderStyle: 'dotted',
            borderColor: 'var(--green-light)',
            '&:nth-child(odd)': { backgroundColor: 'rgba(173,184,78,0.06)' },
            '&:last-child': {
              borderRightWidth: 1,
              borderRightStyle: 'dotted',
              borderColor: 'var(--green-light)',
            },
            '&:first-child': {
              borderLeftWidth: 1,
              borderLeftStyle: 'dotted',
              borderColor: 'var(--green-light)',
            },
          },
          '& tr': {
            borderStyle: 'dotted',
            borderColor: 'var(--green-light)',
            '&:nth-child(odd)': { backgroundColor: 'rgba(173,184,78,0.06)' },
          },
          '& th': {
            borderBottomStyle: 'dotted',
            borderColor: 'var(--green-light)',
            padding: '14px 0 5px 0',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: openMenuLeft ? '' : '100%',
            marginLeft: openMenuLeft ? 30 : '',
            marginRight: openMenuLeft ? 30 : '',
          }}
        >
          <WeekHeader weekDays={weekDays} />
          <TableBody>
            {hours.map((h, i) => (
              <TableRow key={i}>
                <TableCell className={style.hourRow}>
                  <div style={{ maxHeight: '42px' }} className={style.hour}>
                    {h}
                  </div>
                </TableCell>
                {weekDays.map((d) => {
                  const cellEvents = events.filter((event) => shouldShowEvent(event, d, h))
                  const count = cellEvents.length
                  return (
                    <TableCell className={style.eventRow} key={String(d) + String(h)}>
                      <div
                        className={style.containerEvent}
                        style={{ '--event-count': count } as React.CSSProperties}
                      >
                        {cellEvents.map((event) => (
                          <WeekEvent
                            key={event.id}
                            event={event}
                            onEventClick={handleEventClick}
                            title={t}
                            eventCount={count}
                          />
                        ))}
                      </div>
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </div>
        <div className={style.buttonAddAppointmentContainer}>
          <Button onClick={createEvent} className={style.buttonAddAppointment}>
            <img src={addIcon} alt="add" className={style.addIcon} />
            {t('addAppointment')}
          </Button>
        </div>
      </Table>
      {selectedEvent && (
        <Modal open={openModal} className={style.eventModal} onClose={handleCloseModal}>
          <CalendarModal
            event={selectedEvent}
            handleClose={handleCloseModal}
            handleEdit={editEvent}
            handleRemove={deleteEvent}
          />
        </Modal>
      )}
    </PageLayout>
  )
}
